import type { Appointment, AppointmentPayload, AppointmentType, AppointmentUpdatePayload } from './api'
import { dateTimeLocalToUtcIso, utcIsoToDateTimeLocal } from './formatAppointmentWhen'

export const APPOINTMENT_TYPES: AppointmentType[] = ['call', 'visit', 'video']

type AppointmentActor = { id: string; role: string } | null | undefined

export function canViewAllAppointments(user: AppointmentActor): boolean {
  return user?.role === 'management' || user?.role === 'operations' || user?.role === 'super_admin'
}

export function canCreateAppointment(user: AppointmentActor): boolean {
  return user?.role === 'sales_rep' || user?.role === 'super_admin'
}

export function canReadAppointmentSettings(user: AppointmentActor): boolean {
  return canViewAllAppointments(user)
}

export function canAssignAppointment(user: AppointmentActor): boolean {
  return user?.role === 'super_admin'
}

export function canUpdateAppointment(
  user: AppointmentActor,
  appointment: { assigned_user: { id: string } | null },
): boolean {
  if (!user) return false
  if (user.role === 'super_admin') return true
  return appointment.assigned_user?.id === user.id
}

export function canEditAppointment(
  user: AppointmentActor,
  appointment: { assigned_user: { id: string } | null; status: Appointment['status'] },
): boolean {
  return appointment.status === 'scheduled' && canUpdateAppointment(user, appointment)
}

export function canSeeAppointmentLocation(
  user: AppointmentActor,
  appointment: { assigned_user: { id: string } | null },
): boolean {
  if (!user) return false
  if (canViewAllAppointments(user)) return true
  return user.role === 'sales_rep' && appointment.assigned_user?.id === user.id
}

export function canSeeAppointmentNotes(
  user: AppointmentActor,
  appointment: { assigned_user: { id: string } | null },
): boolean {
  if (!user) return false
  if (canViewAllAppointments(user)) return true
  return (user.role === 'sales_rep' || user.role === 'support')
    && appointment.assigned_user?.id === user.id
}

export function appointmentAccountPath(appointment: Pick<Appointment, 'appointable_type' | 'appointable_id'>): string {
  return appointment.appointable_type === 'merchant'
    ? `/merchants/${appointment.appointable_id}`
    : `/leads/${appointment.appointable_id}`
}

export function appointmentAccountLabel(
  appointment: Pick<Appointment, 'appointable_type' | 'account_name'>,
): string {
  const name = appointment.account_name?.trim()
  if (name) return name
  return appointment.appointable_type === 'merchant' ? 'View merchant' : 'View lead'
}

export function statusLabel(status: Appointment['status']): string {
  if (status === 'no_show') return 'No-show'
  return status.charAt(0).toUpperCase() + status.slice(1)
}

export function reminderLabel(minutes: number | null): string {
  if (minutes == null) return 'Reminder not scheduled'
  return `Reminder ${minutes} minutes before`
}

export function reminderHint(canReadSettings: boolean, systemOffset: number | null | undefined): string {
  if (!canReadSettings) {
    return 'Leave blank to use the system lead time, or enter a number of minutes.'
  }
  if (systemOffset == null) {
    return 'System reminder lead time is not set. Leave this blank to skip the reminder.'
  }
  return `Leave blank to use the system lead time of ${systemOffset} minutes.`
}

export const EDIT_REMINDER_HINT = 'Enter minutes before the appointment, or clear the field to remove the reminder.'

export interface AppointmentFormState {
  type: AppointmentType
  startsAt: string
  duration: string
  location: string
  notes: string
  reminder: string
  assigneeId: string
}

export function emptyAppointmentForm(startsAt = ''): AppointmentFormState {
  return {
    type: 'call',
    startsAt,
    duration: '30',
    location: '',
    notes: '',
    reminder: '',
    assigneeId: '',
  }
}

export function appointmentFormFromAppointment(appointment: Appointment): AppointmentFormState {
  return {
    type: appointment.type,
    startsAt: utcIsoToDateTimeLocal(appointment.starts_at),
    duration: String(appointment.duration_minutes),
    location: appointment.location ?? '',
    notes: appointment.notes ?? '',
    reminder: appointment.reminder_offset_minutes == null ? '' : String(appointment.reminder_offset_minutes),
    assigneeId: appointment.assigned_user?.id ?? '',
  }
}

export function parseAppointmentForm(
  values: AppointmentFormState,
): { error: string } | { payload: AppointmentPayload } {
  if (!values.startsAt) {
    return { error: 'Choose a date and time.' }
  }

  const durationMinutes = Number(values.duration)
  if (!Number.isInteger(durationMinutes) || durationMinutes < 5 || durationMinutes > 1440) {
    return { error: 'Duration must be between 5 and 1440 minutes.' }
  }

  const payload: AppointmentPayload = {
    starts_at: dateTimeLocalToUtcIso(values.startsAt),
    duration_minutes: durationMinutes,
    type: values.type,
    location: values.location.trim() || null,
    notes: values.notes.trim() || null,
  }

  if (values.assigneeId) {
    payload.assigned_user_id = values.assigneeId
  }

  if (values.reminder.trim() !== '') {
    const minutes = Number(values.reminder)
    if (!Number.isInteger(minutes) || minutes < 1 || minutes > 10080) {
      return { error: 'Enter a whole number of minutes, or leave the reminder blank.' }
    }
    payload.reminder_offset_minutes = minutes
  }

  return { payload }
}

export function appointmentUpdateFromForm(
  appointment: Appointment,
  values: AppointmentFormState,
  options: {
    canAssign: boolean
    currentUserId?: string
    includeLocation: boolean
    includeNotes: boolean
  },
): { error: string | null; update: AppointmentUpdatePayload; rescheduleAt: string | null } {
  const parsed = parseAppointmentForm(values)
  if ('error' in parsed) {
    return { error: parsed.error, update: {}, rescheduleAt: null }
  }

  const update: AppointmentUpdatePayload = {}
  if (values.type !== appointment.type) update.type = values.type
  if (parsed.payload.duration_minutes !== appointment.duration_minutes) {
    update.duration_minutes = parsed.payload.duration_minutes
  }
  if (options.includeLocation && (parsed.payload.location ?? null) !== (appointment.location ?? null)) {
    update.location = parsed.payload.location ?? null
  }
  if (options.includeNotes && (parsed.payload.notes ?? null) !== (appointment.notes ?? null)) {
    update.notes = parsed.payload.notes ?? null
  }
  if (options.canAssign && options.currentUserId) {
    const nextAssignee = values.assigneeId || options.currentUserId
    if (nextAssignee !== appointment.assigned_user?.id) {
      update.assigned_user_id = nextAssignee
    }
  }

  const nextReminder = values.reminder.trim() === '' ? null : Number(values.reminder)
  if (nextReminder !== (appointment.reminder_offset_minutes ?? null)) {
    update.reminder_offset_minutes = nextReminder
  }

  const rescheduleAt = values.startsAt !== utcIsoToDateTimeLocal(appointment.starts_at)
    ? parsed.payload.starts_at
    : null

  return { error: null, update, rescheduleAt }
}
